<template>
  <LazySection
    :is-height-fixed="!isFetched"
    :placeholder-height="450"
  >
    <div class="reviews-wrap">
      <div class="wrap">
        <section
          v-if="sectionIsShown"
          v-reviews-view-tracker="getReviewsViewEventParams"
          class="reviews"
        >
          <header class="reviews__header">
            <h2 class="reviews__title">
              {{ title }}
            </h2>
            <a
              v-if="reviewLinkShowAllIsShown"
              v-review-load-more-click-tracker="getReviewsViewEventParams"
              class="reviews__link"
              :href="showMoreLink"
            >
              {{ t('section.searchAll') }}
            </a>
          </header>

          <div class="reviews__slider">
            <ui-slider
              :slides="croppedReviews"
              mousewheel
              arrows-always-visible
              :slides-per-view="slidesPerView"
              :space-between="16"
              :options="{ slidesPerGroup }"
            >
              <template #slide="{ index }">
                <review-slide-card
                  :review="croppedReviews[index]"
                  :default-sogu-fields="defaultSoguFields"
                  is-disabled-expand
                  @collapsed-content-clicked="selectedReview = $event"
                />
              </template>

              <template #rightArrowIcon>
                <ArrowRightIcon size="16" />
              </template>
              <template #leftArrowIcon>
                <ArrowRightIcon
                  size="16"
                  class="mirror"
                />
              </template>
            </ui-slider>
          </div>
        </section>
        <ui-button
          v-review-load-more-click-tracker="getReviewsViewEventParams"
          :href="showMoreLink"
          class="reviews__button"
          :variant="UiButtonVariant.OUTLINE"
          :color-mode="UiButtonColorMode.NEUTRAL"
          :size="UiButtonSize.M"
          :text="t('section.searchAll')"
        />
        <review-card-modal
          v-model="selectedReview"
          :default-sogu-fields="defaultSoguFields"
        />
      </div>
    </div>
  </LazySection>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useReviewList, ReviewSlideCard, ReviewCardModal, type ExperienceReview } from '@/entities/review';
  import { reviewsCountLabel } from '@/shared/consts';
  import { choosePlural, useScreenSize } from '@experience/shared';
  import { UiSlider } from '@experience/core-slider-ui';
  import { UiButton, UiButtonColorMode, UiButtonSize, UiButtonVariant } from '@experience/core-button-ui';
  import { useI18n } from '@experience/i18n';
  import ru from '../i18n/ru.json';
  import { MAX_SLIDES_COUNT, SLIDES_PER_VIEW, RATE_MAX, RATE_MIN } from '../lib/consts';
  import { ArrowRightIcon } from '@/shared/ui/icons/arrowRightIcon';
  import { useSlidesPerView } from '@/shared/lib/use-slides-per-view';
  import { useClickTracker, useViewTracker } from '@/entities/sogu';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import { useExcursionFiltersStore } from '@/entities/filter/store/excursion';
  import { makeFullLink } from '@/shared/lib/makeFullLink';
  import { tripsterStandard } from '@experience/money-utils';
  import { LazySection } from '@/shared/ui/LazySections';

  interface Props {
    slug: string;
    defaultSoguFields: ContextGeoSubject;
  }

  const props = defineProps<Props>();

  const filterStore = useExcursionFiltersStore();

  const { reviewsList, reviewsCount, isFetched } = useReviewList({
    city__slug: computed(() => props.slug),
    limit: computed(() => MAX_SLIDES_COUNT),
    tag__slug: computed(() => filterStore.state.tag__slugs || ''),
    rate_max: RATE_MAX,
    rate_min: RATE_MIN,
    is_long: 1,
  });

  const croppedReviews = computed(() => {
    const reviewsSubset = [...toValue(reviewsList).slice(0, 15)];

    reviewsSubset.sort((a, b) => {
      const photoCountA = a.photos?.length || 0;
      const photoCountB = b.photos?.length || 0;

      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();

      return photoCountB - photoCountA || dateB - dateA;
    });

    return reviewsSubset;
  });

  const route = useRoute();
  const { isMobile } = useScreenSize();

  const getReviewsViewEventParams = () => props.defaultSoguFields.soguStamp;

  const vReviewsViewTracker = useViewTracker('listing:reviews.view');

  const vReviewLoadMoreClickTracker = useClickTracker('listing:reviews:more-button.click');

  const totalReviews = computed(() => reviewsList.value?.length ?? 0);

  const slidesPerView = ref(0);
  const selectedReview = ref<ExperienceReview | null>(null);

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const slidesPerGroup = computed(() => (isMobile.value ? 1 : slidesPerView.value));

  const title = computed(
    () =>
      `${tripsterStandard(reviewsCount.value)} ${choosePlural(reviewsCount.value, reviewsCountLabel.items_pluralized)} ${t('section.titleLabel')}`,
  );

  const sectionIsShown = computed(() => reviewsCount.value > 0);

  const reviewLinkShowAllIsShown = computed(
    () =>
      reviewsCount.value > MAX_SLIDES_COUNT ||
      (reviewsCount.value > totalReviews.value && totalReviews.value < MAX_SLIDES_COUNT),
  );

  const updateSlidesPerView = () => {
    slidesPerView.value = useSlidesPerView(totalReviews.value, ...SLIDES_PER_VIEW);
  };

  const showMoreLink = computed(() => {
    const { citySlug, tag } = route.params;

    return tag
      ? makeFullLink(`/experience/${citySlug}/${tag}/reviews/`)
      : makeFullLink(`/experience/${citySlug}/reviews/`);
  });

  onMounted(() => {
    updateSlidesPerView();
    window.addEventListener('resize', updateSlidesPerView);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateSlidesPerView);
  });
</script>

<style scoped lang="scss">
  @use '@/shared/styles/components/slider' as slider;
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .reviews-wrap {
    position: relative;
    overflow: hidden;
  }

  .reviews {
    overflow: hidden;
    margin-top: 84px;
    margin-right: -20px;

    @include adaptivity.mf-mobile-l {
      margin-top: 96px;
    }

    @include adaptivity.mf-tablet {
      overflow: visible;
      margin-top: 80px;
      margin-right: 0;
    }
  }

  .reviews__header {
    display: flex;
    gap: 24px;
    align-items: flex-end;
  }

  .reviews__title {
    margin: 0;
    color: var(--alias-color-text-default);

    @include typography.base-700-500-extra;

    @include adaptivity.mf-desktop {
      @include typography.base-800-750-extra;
    }
  }

  .mirror {
    transform: scaleX(-1);
  }

  .reviews__slider {
    margin-top: 16px;

    @include slider.mobile-slider;
  }

  .reviews__link {
    display: none;
    color: var(--alias-color-link-default);
    text-decoration: none;

    @include typography.base-500-400-regular;

    @include adaptivity.mf-laptop {
      display: block;
    }
  }

  .reviews__button {
    width: 100% !important;
    margin-top: 16px;
    text-wrap: nowrap;

    @include adaptivity.mf-tablet {
      display: block !important;
      width: 159px;
      margin-top: 30px;
      margin-right: auto;
      margin-left: auto;
    }

    @include adaptivity.mf-laptop {
      display: none !important;
    }
  }
</style>
